import React, { useRef } from 'react'
import { useParams } from 'react-router-dom';
import { Button, Container } from 'reactstrap';
import { useAuth } from '../../Services/AuthContext';
import { useFetchCached } from '../../Services/fetchApi';
import useFetch from '../../Services/useFetch';
import { endpoints } from '../../utils/Constants';
import LoadingAnimation from '../Form/LoadingAnimation';
import DocumentDetails from '../Editors/DocumentDetails';
import SupplierDocumentUploadGuidance from '../Form/SupplierDocumentUploadGuidance';
import { IDocumentRequirementItem } from '../../Models/IDocumentRequirementItem';
import { ISupplierDocumentUploadPolicy } from '../../Models/ISupplierDocumentUploadPolicy';
import '../Editors/DocumentDetails.css';

interface DocumentRequirementGroup {
    groupName: string;
    categoryHelpText?: string;
    requirements: IDocumentRequirementItem[];
}

const compareRequirementNames = (left: IDocumentRequirementItem, right: IDocumentRequirementItem) =>
    (left.name || left.document?.fileCategory || '').localeCompare(right.name || right.document?.fileCategory || '', undefined, { sensitivity: 'base' });

// Sub-groups within the tab by GroupName (falling back to Category) so documents added to the same group co-locate.
const groupByGroupName = (requirements: IDocumentRequirementItem[]) => requirements.reduce<DocumentRequirementGroup[]>((groups, requirement) => {
    const groupName = requirement.groupName || requirement.category || 'Required Documents';
    let group = groups.find(g => g.groupName === groupName);
    if (!group) {
        group = { groupName, categoryHelpText: requirement.categoryHelpText, requirements: [] };
        groups.push(group);
    }

    if (!group.categoryHelpText && requirement.categoryHelpText) {
        group.categoryHelpText = requirement.categoryHelpText;
    }

    group.requirements.push(requirement);
    return groups;
}, []);

const SupplierInsuranceDocumentList = ({ route, supplierId: supp, charterRequestId }: { route?: string, supplierId?: string, charterRequestId?: string }) => {
    const params = useParams<Record<"supplierId", string>>();
    const [auth] = useAuth();
    const supplierId = params.supplierId ?? auth.supplierId ?? supp;
    const requirementsUrl = supplierId ? endpoints.getSupplierDocumentRequirements + supplierId + (charterRequestId ? '?charterRequestId=' + encodeURIComponent(charterRequestId) : '') : null;
    const [requirements, loading, error, refresh] = useFetch<IDocumentRequirementItem[]>(requirementsUrl);
    const [uploadPolicy] = useFetchCached<ISupplierDocumentUploadPolicy>(endpoints.getSupplierDocumentUploadPolicy);
    // Keep showing the last loaded documents if a background refresh (e.g. after upload) fails.
    const lastRequirementsRef = useRef<IDocumentRequirementItem[] | undefined>(undefined);
    if (requirements) lastRequirementsRef.current = requirements;
    const displayRequirements = requirements ?? lastRequirementsRef.current;
    const initialLoad = loading && !displayRequirements;
    const insuranceRequirements = displayRequirements?.filter(r => r.isInsurance);
    const requiredInsuranceRequirements = insuranceRequirements?.filter(r => r.isRequired) ?? [];
    const optionalInsuranceRequirements = insuranceRequirements?.filter(r => !r.isRequired) ?? [];
    const requiredRequirementGroups = groupByGroupName(requiredInsuranceRequirements);
    const optionalRequirementGroups = groupByGroupName(optionalInsuranceRequirements);
    const sortedRequiredRequirements = requiredInsuranceRequirements.slice().sort(compareRequirementNames);
    const sortedOptionalRequirements = optionalInsuranceRequirements.slice().sort(compareRequirementNames);
    const displayedRequiredHints = new Set<string>();
    const displayedOptionalHints = new Set<string>();
    const canSeeGuidance = auth.role === 'Admin' || auth.role === 'Supplier';

    return <Container className="doc-list">
        {canSeeGuidance ? <SupplierDocumentUploadGuidance policy={uploadPolicy} isAdmin={auth.role === 'Admin'} isSupplier={auth.role === 'Supplier'} /> : null}
        {initialLoad ? <LoadingAnimation /> : error ? <h3>Error<Button onClick={() => refresh()}>Retry</Button></h3> : null}
        {requiredRequirementGroups.length > 0 && <>
            <h3 className="doc-list__section-title">Required Documents</h3>
            {sortedRequiredRequirements.map((requirement, i) => {
                const group = requiredRequirementGroups.find(candidate => candidate.groupName === (requirement.groupName || requirement.category || 'Required Documents'));
                const showHint = group !== undefined && Boolean(group.categoryHelpText) && !displayedRequiredHints.has(group.groupName);
                if (showHint && group) {
                    displayedRequiredHints.add(group.groupName);
                }

                return <React.Fragment key={requirement.code || i}>
                    {canSeeGuidance && showHint && group ? <div className="doc-list__help-badge">{group.categoryHelpText}</div> : null}
                    <DocumentDetails doc={requirement.document} refresh={refresh} supplierId={supplierId} uploadPolicy={uploadPolicy} />
                </React.Fragment>;
            })}
        </>}
        {sortedOptionalRequirements.length > 0 && <>
            <h3 className="doc-list__section-title">Optional Documents</h3>
            {sortedOptionalRequirements.map((requirement, i) => {
                const group = optionalRequirementGroups.find(candidate => candidate.groupName === (requirement.groupName || requirement.category || 'Required Documents'));
                const showHint = group !== undefined && Boolean(group.categoryHelpText) && !displayedOptionalHints.has(group.groupName);
                if (showHint && group) {
                    displayedOptionalHints.add(group.groupName);
                }

                return <React.Fragment key={requirement.code || i}>
                    {canSeeGuidance && showHint && group ? <div className="doc-list__help-badge">{group.categoryHelpText}</div> : null}
                    <DocumentDetails optional doc={requirement.document} refresh={refresh} supplierId={supplierId} uploadPolicy={uploadPolicy} />
                </React.Fragment>;
            })}
        </>}
    </Container>

}

export default SupplierInsuranceDocumentList;



